"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import type { ComponentProps } from "react";
import { storyblokEditable } from "@storyblok/react/rsc";
import Autoplay from "embla-carousel-autoplay";
import { useLightboxOpen } from "@/components/layout/LightboxProvider";

import { SectionHeader } from "@/components/typography/SectionHeader";
import type { SectionSize } from "@/components/typography/SectionHeader";
import type { TestimonialSectionStoryblok } from "@/component-types-sb";
import Testimonial from "./Testimonial";
import { cn } from "@/lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  GalleryNavPrevious,
  GalleryNavNext,
  type CarouselApi,
} from "@/components/ui/carousel";

type TestimonialSectionProps = ComponentProps<"section"> & {
  blok: TestimonialSectionStoryblok;
  size?: SectionSize;
};

const TestimonialSection = ({
  blok,
  size = "tall",
  ...rest
}: TestimonialSectionProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [canScroll, setCanScroll] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const lightboxOpen = useLightboxOpen();
  const autoplayRef = useRef(
    Autoplay({
      delay: 3000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    }),
  );

  const checkCanScroll = useCallback(() => {
    if (!api) return;
    setCanScroll(api.canScrollNext() || api.canScrollPrev());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    checkCanScroll();
    api.on("resize", checkCanScroll);
    api.on("reInit", checkCanScroll);
    return () => {
      api.off("resize", checkCanScroll);
      api.off("reInit", checkCanScroll);
    };
  }, [api, checkCanScroll]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const autoplay = autoplayRef.current;
    if (!api) return;
    if (canScroll && isInView && !lightboxOpen) {
      autoplay.play();
    } else {
      autoplay.stop();
    }
  }, [api, canScroll, isInView, lightboxOpen]);

  return (
    <section
      ref={sectionRef}
      className={cn("p-4 md:p-14", rest.className)}
      {...storyblokEditable(blok)}
    >
      <SectionHeader
        tagline={blok.subline}
        headline={blok.headline}
        size={size}
      />

      <Carousel
        setApi={setApi}
        opts={{
          align: "center",
          loop: false,
          dragFree: false,
          watchResize: true,
          breakpoints: {
            "(min-width: 768px)": {
              align: "start",
              dragFree: true,
            },
          },
        }}
        plugins={[autoplayRef.current]}
        className="w-full relative"
      >
        <GalleryNavPrevious show={canScroll} />
        <CarouselContent
          className={
            canScroll
              ? "md:ml-0 md:pl-8 items-stretch"
              : "justify-center items-stretch"
          }
        >
          {blok.items?.map((nestedBlok: any) => (
            <CarouselItem
              key={nestedBlok.uuid}
              className={
                canScroll
                  ? "basis-[85%] md:basis-auto md:pl-4 pl-3 h-auto"
                  : "basis-[85%] md:basis-auto px-2 h-auto"
              }
            >
              <Testimonial blok={nestedBlok} />
            </CarouselItem>
          ))}
          {canScroll && <div className="shrink-0 w-px md:w-8" />}
        </CarouselContent>
        <GalleryNavNext show={canScroll} />
      </Carousel>
    </section>
  );
};

export default TestimonialSection;
